import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import TextField from "@mui/material/TextField";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";

import { useDispatch, useSelector } from "react-redux";
import Checkbox from "@mui/material/Checkbox";
import { Box, FormControlLabel, Chip, Button } from "@mui/material";
import { DashboardLocIdInfo } from "../../../../redux/actions/dashboard/dashboardTypes";
import { State } from "../../../../redux/slices";
import { ThunkDispatch } from "@reduxjs/toolkit";

import {
  resetStateCreatLocUsers,
  setMultiLocError,
  setValuesAutCompleteStep2,
} from "../../../../redux/slices/location/createLocation";

import { useNavigate } from "react-router-dom";
import { linkUsersToLocationFinalStep } from "../../../../redux/actions/users/userAsyncActions";
import { LocationGrainTrackerValues } from "../../../../redux/slices/grainTracker/grainTrackerTypes";
import { fetchUsersToLinkInToLocation } from "../../../../redux/actions/locations/locationDetailsAsync";
import { resetUser } from "../../../../redux/slices/fleet/locations";
import { logoutGrainTracker } from "../../../../redux/slices/grainTracker/grainTracker";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const SetActions = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

  return {
    linkUsersToLocationFinalStep: (
      locationGrainTrackerValues: LocationGrainTrackerValues,
      logOutGrainTracker: any,
    ) => {
      dispatch(
        linkUsersToLocationFinalStep({
          locationGrainTrackerValues,
          logOutGrainTracker,
        }),
      );
    },

    setValues: (values: any) => {
      dispatch(setValuesAutCompleteStep2(values));
    },
    removeError: () => {
      dispatch(setMultiLocError(""));
    },

    cancel: () => {
      dispatch(resetStateCreatLocUsers());
    },

    fetchUsersToLinkInToLocation: () => {
      dispatch(fetchUsersToLinkInToLocation(null));
    },
    resetUsers: () => {
      dispatch(resetUser());
    },
  };
};

type LinkUsersToLocationParams = {
  isCreatingWithGrainTracker: boolean;
  locationGrainTrackerValues: LocationGrainTrackerValues;
};

export const StepTwoNoCreateUsers: FC<LinkUsersToLocationParams> = ({
  isCreatingWithGrainTracker,
  locationGrainTrackerValues,
}) => {
  const [t] = useTranslation("global");
  const { users } = useSelector((state: State) => state.LocationsReducer);

  const {
    userSelectedAutocompleteStep2NoUsers,
    multiLocationCreatedSuccess,
    multilocError,
  } = useSelector((state: State) => state.CreateLocationReducer);

  const {
    linkUsersToLocationFinalStep,
    setValues,
    cancel,
    removeError,
    resetUsers,
    fetchUsersToLinkInToLocation,
  } = SetActions();
  const navigate = useNavigate();

  const usersFilterOptions = users.data.filter(
    (values) => values.associatedGeoPoint === null,
  );

  const usersOptions = usersFilterOptions.map((values) => ({
    name: values.username,
    id: values.id,
  }));

  const userSelected = userSelectedAutocompleteStep2NoUsers.valuesSelected.map(
    (values) => values.id,
  );

  const AutoCompleteLinkUsersCreateLocations = () => {
    const loading = false;
    const transform = usersOptions?.map((c: any) => c);

    return (
      <Autocomplete
        sx={{
          height: "57px",
        }}
        disabled={loading}
        multiple
        disableListWrap={true}
        options={usersOptions as any}
        disableCloseOnSelect
        onClose={() => resetUsers()}
        filterOptions={(options, params) => {
          const filter = createFilterOptions();
          const filtered = filter(options, params as any);
          return [
            { name: t("Dashboard.setAllLocations"), id: 0 },
            ...filtered,
          ] as any;
        }}
        value={userSelectedAutocompleteStep2NoUsers.valuesSelected as any}
        getOptionLabel={(option: DashboardLocIdInfo) => option.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onChange={(_e, value, reason) => {
          if (reason === "clear" || reason === "removeOption")
            setValues({
              allValuesSelected: false,
              valuesSelected: [],
            });

          if (reason === "selectOption" && value.length === usersOptions.length)
            setValues({
              allValuesSelected: true,
              valuesSelected: transform as any,
            });

          setValues({ allValuesSelected: false, valuesSelected: value });

          if (reason === "selectOption" && usersOptions.length === 0)
            console.log("");
        }}
        renderInput={(params: { InputLabelProps: any }) => {
          return (
            <TextField
              {...params}
              label={t("Create_site.not_create_users.select")}
              variant="outlined"
              size="medium"
              onClick={() => fetchUsersToLinkInToLocation()}
              sx={{
                width: "40vw",
                maxWidth: "calc(100% - 20px)",
                marginBottom: "24px",
                marginLeft: "24px",
                marginTop: "24px",
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          );
        }}
        renderTags={(list) => {
          if (list.length === 1) {
            return (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {list.map((value: any) => (
                  <Chip key={value.id} label={value.name} />
                ))}
              </Box>
            );
          }

          if (list.length > 1) {
            return (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                <Chip
                  label={`${list[0].name} + ${list.length - 1} ${t(
                    "Common_options.others",
                  )}`}
                />
              </Box>
            );
          }
        }}
        renderOption={(props, option, { selected }) => {
          if (option.id === 0) {
            return (
              <Box
                key={option.id}
                onMouseDown={(e) => e.preventDefault()}
                pl={1.5}
                py={0.5}
                sx={{
                  position: "sticky",
                  top: "-1rem",
                  background: "#fff",
                  height: "60px",
                  zIndex: "2",
                  borderBottom: "1px solid",
                }}
              >
                <FormControlLabel
                  onClick={(e) => {
                    if (usersOptions.length >= 1) {
                      e.preventDefault();
                      !userSelectedAutocompleteStep2NoUsers.allValuesSelected
                        ? setValues({
                            allValuesSelected: true,
                            valuesSelected: transform,
                          })
                        : setValues({
                            allValuesSelected: false,
                            valuesSelected: [],
                          });
                    }
                  }}
                  disabled={usersOptions.length === 0}
                  label={t("Create_site.not_create_users.select")}
                  control={
                    <Checkbox
                      disabled={usersOptions.length === 0}
                      checked={
                        userSelectedAutocompleteStep2NoUsers.allValuesSelected
                      }
                    />
                  }
                />
              </Box>
            );
          }

          return (
            <li {...props}>
              <Checkbox
                key={option.id}
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={
                  option.id === 0
                    ? !!(
                        userSelectedAutocompleteStep2NoUsers.valuesSelected.filter(
                          (x) => x.id !== undefined,
                        ).length === usersOptions.length
                      )
                    : selected
                }
              />

              <div>{`${option.name}`}</div>
            </li>
          );
        }}
        renderGroup={(params) => params as unknown as React.ReactNode}
      />
    );
  };

  if (multiLocationCreatedSuccess) {
    navigate("/locations");
    setTimeout(() => {
      cancel();
    }, 2000);
  }

  if (multilocError) {
    setTimeout(() => {
      removeError();
    }, 20000);
  }

  useEffect(() => {
    setValues({
      allValuesSelected: false,
      valuesSelected: [],
    });
  }, []);

  return (
    <div className="steps-container-create-loc">
      <div className="step-title">
        {t("Common_options.step")} {isCreatingWithGrainTracker ? "3/3 " : "2/2"}
      </div>
      <div className="step-title-create">
        {t("Create_site.not_create_users.link")}
      </div>
      <div className="design-scope">
        {t("Create_site.not_create_users.link_desc")}
      </div>
      <div className="autocomplete-container-locations">
        {AutoCompleteLinkUsersCreateLocations()}
      </div>
      <div className="container-button-create-location-link-users">
        <Button
          onClick={() =>
            linkUsersToLocationFinalStep(
              locationGrainTrackerValues,
              logoutGrainTracker,
            )
          }
          variant="contained"
          sx={{ textTransform: "none" }}
        >
          {userSelected.length > 0
            ? t("Create_site.not_create_users.link_users")
            : t("Create_site.not_create_users.continue_no_link")}
        </Button>
      </div>

      <div className="background-components-fix" />
    </div>
  );
};

export default StepTwoNoCreateUsers;
